// src/pages/TransactionsPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { getTransactions, getAllSocieties } from '../services/api.service';
import TransactionList from '../components/transaction-list';
import Loading from '../components/loading';
import { useNavigate } from 'react-router-dom';
import Helper from '../services/helper.service';

const normalize = (s = "") =>
    s.toString().toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");

const getTxDate = (t) => new Date(t?.createdAt || t?.date || t?.timestamp || 0);
const endOfDay = (yyyyMmDd) => {
    if (!yyyyMmDd) return null;
    const d = new Date(yyyyMmDd);
    d.setHours(23, 59, 59, 999);
    return d;
};

// --- helpers for society ---
const getSocietyId = (t) => {
    // t.societyId can be a string or an object {_id: "..."}
    if (!t?.societyId) return null;
    if (typeof t.societyId === 'string') return t.societyId;
    if (typeof t.societyId === 'object' && t.societyId._id) return t.societyId._id;
    return null;
};

const buildSocietyLabel = (t, mapById) => {
    // Prefer populated name on the transaction
    if (t?.society?.name) return t.society.name;
    if (t?.societyId && typeof t.societyId === 'object' && t.societyId.name) return t.societyId.name;

    // Then try the full societies list we loaded separately
    const id = getSocietyId(t);
    if (id && mapById.has(id)) return mapById.get(id);

    // Fallbacks
    if (t?.subdomain) return t.subdomain;
    return id ? `#${id.slice(-6)}` : '—';
};

const TransactionsPage = () => {
    const [transactions, setTransactions] = useState([]);
    const [societies, setSocieties] = useState([]);
    const [loading, setLoading] = useState(true);

    // filters
    const [search, setSearch] = useState("");
    const [fromDate, setFromDate] = useState(""); // yyyy-mm-dd
    const [toDate, setToDate] = useState("");     // yyyy-mm-dd
    const [societyFilter, setSocietyFilter] = useState(""); // society _id or ""

    const navigate = useNavigate();

    useEffect(() => {
        const fetch = async () => {
            setLoading(true);
            try {
                // Load both transactions and societies
                const [tx, socs] = await Promise.all([
                    getTransactions(),
                    getAllSocieties().catch(() => []) // be resilient if this fails
                ]);
                setTransactions(Array.isArray(tx) ? tx : []);
                setSocieties(Array.isArray(socs) ? socs : []);
            } catch {
                Helper.toast("Erreur lors du chargement", "error");
            } finally {
                setLoading(false);
            }
        };
        fetch();
    }, []);

    // Map societies by id -> name (with light fallbacks)
    const societyMap = useMemo(() => {
        const map = new Map();
        for (const s of societies) {
            const id = s?._id;
            if (!id) continue;
            const label =
                s.name ||
                (s.website ? new URL(s.website).hostname.replace(/^www\./, '') : '') ||
                s.email ||
                s.siret ||
                `#${String(id).slice(-6)}`;
            map.set(id, label);
        }
        return map;
    }, [societies]);

    // Build unique society list from current transactions (for the select filter)
    const societyOptions = useMemo(() => {
        const map = new Map();
        for (const t of transactions) {
            const id = getSocietyId(t);
            if (!id) continue;
            if (!map.has(id)) {
                map.set(id, buildSocietyLabel(t, societyMap));
            }
        }
        return Array.from(map.entries())
            .map(([value, label]) => ({ value, label }))
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    }, [transactions, societyMap]);

    // Base filtering
    const filtered = useMemo(() => {
        const terms = normalize(search).trim().split(/\s+/).filter(Boolean);
        const start = fromDate ? new Date(fromDate) : null;
        const end = endOfDay(toDate);

        return transactions.filter((t) => {
            // Search filter (firstname, lastname, email, phone)
            const combined = normalize([
                t?.customer?.firstname,
                t?.customer?.lastname,
                t?.customer?.email,
                t?.customer?.phone,
            ].filter(Boolean).join(" "));

            const matchesSearch = terms.every(term => combined.includes(term));

            // Date filter (inclusive)
            const txDate = getTxDate(t);
            const matchesFrom = start ? txDate >= start : true;
            const matchesTo = end ? txDate <= end : true;

            // Society filter
            const txSocietyId = getSocietyId(t);
            const matchesSociety = societyFilter ? (txSocietyId === societyFilter) : true;

            return matchesSearch && matchesFrom && matchesTo && matchesSociety;
        });
    }, [transactions, search, fromDate, toDate, societyFilter]);

    // Augment filtered transactions with society name + a display label next to the billing number
    const filteredWithSociety = useMemo(() => {
        return filtered.map((t) => {
            const societyName = buildSocietyLabel(t, societyMap);
            const billingNumberLabel =
                t?.billingNumber != null
                    ? `${t.billingNumber} • ${societyName}`
                    : societyName;

            return {
                ...t,
                societyName,          // new field: for rendering/exports
                billingNumberLabel,   // new field: "12345 • ACME"
                // If your TransactionList strictly renders t.billingNumber,
                // you can uncomment the next line to override it with a string:
                // billingNumber: billingNumberLabel,
            };
        });
    }, [filtered, societyMap]);

    const clearFilters = () => {
        setSearch("");
        setFromDate("");
        setToDate("");
        setSocietyFilter("");
    };

    const handleRowClick = (t) => {
        if (t?.type === 'payment-link') {
            const params = new URLSearchParams();
            if (t.paymentLinkId) params.set('focus', t.paymentLinkId);
            if (!params.has('focus') && t.metadataId) params.set('focus', t.metadataId);
            if (t.transactionId) params.set('tx', t.transactionId);
            if (t.amount != null) params.set('amount', String(t.amount));
            if (t.date) params.set('txDate', new Date(t.date).toISOString());
            navigate(`/payment-link${params.toString() ? `?${params.toString()}` : ''}`);
            return;
        }
        if (t?.type === 'services') {
            navigate(`/${t.metadataId}`);
            return;
        }
        navigate(`/${t.metadataId}`);
    };

    return (
        <section className="max-w-screen-2xl mx-auto p-6">
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-2xl font-bold">Toutes les transactions</h1>
            </div>

            {/* Filters */}
            <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6 items-end">
                <div className="lg:col-span-2">
                    <input
                        type="text"
                        placeholder="Rechercher (nom, prénom, email, téléphone)"
                        className="h-[44px] w-full border border-gray-300 rounded px-3 py-2"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                <div className="flex flex-col">
                    <label className="mb-1 text-sm font-medium text-gray-700">
                        Créée après le
                    </label>
                    <input
                        type="date"
                        className="border border-gray-300 rounded px-3 py-2"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        aria-label="Date de début"
                    />
                </div>

                <div className="flex flex-col">
                    <label className="mb-1 text-sm font-medium text-gray-700">
                        Créée avant le
                    </label>
                    <input
                        type="date"
                        className="border border-gray-300 rounded px-3 py-2"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        aria-label="Date de fin"
                    />
                </div>

                {/* Society filter */}
                <div className="flex flex-col">
                    <label className="mb-1 text-sm font-medium text-gray-700">
                        Société
                    </label>
                    <select
                        className="h-[44px] border border-gray-300 rounded px-3 py-2"
                        value={societyFilter}
                        onChange={(e) => setSocietyFilter(e.target.value)}
                        aria-label="Filtrer par société"
                    >
                        <option value="">Toutes les sociétés</option>
                        {societyOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                <div className="flex gap-2">
                    <button
                        onClick={clearFilters}
                        className="h-[44px] w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100"
                    >
                        Réinitialiser
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-24">
                    <Loading />
                </div>
            ) : (
                <TransactionList
                    transactions={filteredWithSociety}
                    onRowClick={handleRowClick}
                    onRefundSuccess={(refundedId) => {
                        console.log("Remboursement terminé pour la transaction :", refundedId);
                        getTransactions()
                            .then((data) => setTransactions(data || []))
                            .catch(() => Helper.toast("Erreur lors du rechargement", "error"));
                    }}
                />
            )}
        </section>
    );
};

export default TransactionsPage;